import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Password box with a show/hide toggle. Use this anywhere the user types a
// password, so the reveal affordance behaves the same everywhere.
//
// The toggle is explicitly `type="button"`. Inside a form, a button without it
// defaults to `submit` — so revealing your password would try to log you in
// with a half-typed one. shadcn's `Button` does not set this for you.
//
// Starts hidden and returns to hidden on unmount (state is per-mount) — the
// reveal is a deliberate act each time, not a preference we remember. Focus
// stays put when toggling, so a user who reveals mid-typing can keep going.
//
// The toggle stays in the tab order. Skipping it would make the reveal
// mouse-only, which is backwards: the people most likely to need "did I type
// that right?" are the ones not looking at a pointer.
export interface PasswordInputProps
  extends Omit<React.ComponentProps<'input'>, 'type' | 'className'> {
  /** Styles the wrapper. */
  className?: string;
  /** Styles the inner input element. */
  inputClassName?: string;
}

export function PasswordInput({
  className,
  inputClassName,
  disabled,
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = React.useState(false);
  const Icon = visible ? EyeOff : Eye;

  return (
    <div className={cn('relative', className)}>
      <Input
        {...props}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        // Room for the toggle so a long password doesn't run underneath it.
        className={cn('pr-9', inputClassName)}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        // Follows the field: a disabled password can't be corrected, so there
        // is nothing to check by revealing it.
        disabled={disabled}
        onClick={() => setVisible((v) => !v)}
        // The label names the ACTION, not the state — what a screen reader
        // user needs in order to decide whether to press it.
        aria-label={visible ? 'Hide password' : 'Show password'}
        // Announces the state for anyone who wants it, so the label doesn't
        // have to carry both.
        aria-pressed={visible}
        // `size="icon"` is size-9, matching the h-9 Input exactly, so the
        // control sits flush in the field's trailing edge.
        className="absolute top-0 right-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
      >
        <Icon className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
